import { Injectable } from '@angular/core';
import { UsersApiService } from '@epicuro-next/data/users';
import {
  clearActiveAccount,
  selectActiveAccount,
  setActiveAccount,
  upsertAccounts,
} from '@epicuro-next/state/accounts-state';
import { loginSuccess } from '@epicuro-next/state/user-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Account } from 'graphql-api/gql-api-interfaces';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import { BasketAccountsService } from '../../services';
import * as basketActions from '../actions';
import { selectActiveBasketAccounts, selectActiveBasketId } from '../selectors';

/**
 * Effect to implement Accounts Selector and Basket flows
 * https://epicuro.atlassian.net/wiki/spaces/PP/pages/2153742381/Account+Selector+User+Flows
 */

@Injectable()
export class BasketAccountsEffects {
  constructor(
    private actions$: Actions,
    private userService: UsersApiService,
    private store: Store,
    private basketAccountsService: BasketAccountsService,
  ) {}

  /**
   * Listen set active account ONCE after a User selects them or UI gets it from storage
   * Note: changing active account from menu should NOT trigger this flow
   * Get active basket id
   * Case A: If a User has basket and only active account - load basket, load basket account and set them as selected
   * Case B: If a User has basket and selected accounts - load basket accounts, compare them and if they are equals, load basket.
   * If no - clear basket state. The new one basket will be created after adding card
   */
  public setActiveAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginSuccess),
      switchMap(() => {
        return this.actions$.pipe(
          ofType(setActiveAccount),
          // Only initial set account action is listened
          take(1),
        );
      }),
      withLatestFrom(
        this.store.pipe(
          select(selectActiveAccount),
          filter<Account>((acc) => !!acc),
        ),
        this.store.pipe(select(selectActiveBasketAccounts)),
      ),
      switchMap(([_, account, basketAccounts]) => {
        return this.userService.getActiveBasketId(account.accountId).pipe(
          switchMap((basketId) => {
            // load basket, load basket account and set them as selected
            if (!basketAccounts.length) {
              return [
                basketActions.setActiveBasketId({ basketId }),
                basketActions.resolveActiveBasketComplete(),
                basketActions.loadActiveBasket(),
                basketActions.loadBasketAccounts({ basketId }),
              ];
            }
            // compare basket accounts and selected accounts
            return [
              basketActions.compareBasketAndAccountsSelection({
                basketId,
                selectedAccounts: basketAccounts,
              }),
            ];
          }),
          catchError(() => {
            const actions: Action[] = [
              upsertAccounts({ accounts: basketAccounts }),
              basketActions.resolveActiveBasketComplete(),
            ];

            if (
              !basketAccounts.length ||
              basketAccounts.every(
                (basketAccount) =>
                  basketAccount.accountId !== account.accountId,
              )
            ) {
              actions.push(
                basketActions.setActiveBasketAccounts({
                  basketAccounts: [
                    {
                      ...account,
                      shipToAddresses: account.shipToAddresses.map(
                        (address) => ({ ...address, hasDeliveryItems: true }),
                      ),
                    },
                  ],
                }),
              );
            }
            return actions;
          }),
        );
      }),
    ),
  );

  public compareBasketAndAccountsSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.compareBasketAndAccountsSelection),
      switchMap(({ basketId, selectedAccounts }) =>
        this.basketAccountsService.getBasketAccounts({ basketId }).pipe(
          switchMap((basketAccounts) => {
            if (
              this.basketAccountsService.isEqualAccounts(
                basketAccounts,
                selectedAccounts,
              )
            ) {
              return [
                basketActions.setActiveBasketId({ basketId }),
                basketActions.resolveActiveBasketComplete(),
                basketActions.loadActiveBasket(),
                upsertAccounts({ accounts: basketAccounts }),
              ];
            }
            return [basketActions.resolveActiveBasketComplete()];
          }),
          catchError((err) =>
            of(
              basketActions.compareBasketAndAccountsSelectionFailure({
                error: err.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public loadBasketAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.loadBasketAccounts),
      switchMap(({ basketId }) =>
        this.basketAccountsService.getBasketAccounts({ basketId }).pipe(
          withLatestFrom(this.store.pipe(select(selectActiveBasketId))),
          switchMap(([basketAccounts, activeBasketId]) => {
            const actions: Action[] = [
              upsertAccounts({ accounts: basketAccounts }),
            ];

            if (activeBasketId === basketId) {
              actions.push(
                basketActions.setActiveBasketAccounts({ basketAccounts }),
              );
            } else {
              actions.push(
                basketActions.setOpenedBasketAccounts({ basketAccounts }),
              );
            }

            return actions;
          }),
          catchError((err) =>
            of(basketActions.loadBasketAccountsFailure({ error: err.message })),
          ),
        ),
      ),
    ),
  );

  public setBasketToAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.setBasketToAccount),
      switchMap(({ accountId, basketId }) =>
        this.userService.setActiveBasketId(accountId, basketId).pipe(
          withLatestFrom(this.store.pipe(select(selectActiveBasketId))),
          filter(([_, activeBasketId]) => activeBasketId !== basketId),
          map(() => basketActions.setActiveBasketId({ basketId })),
          catchError((error) =>
            of(
              basketActions.setActiveBasketIdFailure({ error: error.message }),
            ),
          ),
        ),
      ),
    ),
  );

  public updateActiveBasketAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.updateActiveBasketAccounts),
      withLatestFrom(
        this.store.pipe(
          select(selectActiveBasketId),
          filter<string>((id) => !!id),
        ),
      ),
      switchMap(([_, basketId]) =>
        this.basketAccountsService.getBasketAccounts({ basketId }).pipe(
          withLatestFrom(
            this.store.pipe(
              select(selectActiveAccount),
              filter<Account>(Boolean),
            ),
          ),
          switchMap(([basketAccounts, currentActiveAccount]) => {
            const isActiveAccountChanged = !basketAccounts.some(
              (account) => account.accountId === currentActiveAccount.accountId,
            );

            const actions: Action[] = [
              upsertAccounts({ accounts: basketAccounts }),
              basketActions.setActiveBasketAccounts({ basketAccounts }),
            ];

            if (isActiveAccountChanged) {
              const newActiveAccount =
                basketAccounts.find(
                  (selected) =>
                    selected.accountId === currentActiveAccount.accountId,
                ) || basketAccounts[0];
              actions.push(setActiveAccount({ account: newActiveAccount }));
              actions.push(
                basketActions.setBasketToAccount({
                  accountId: newActiveAccount.accountId,
                  basketId,
                }),
              );
            }

            return actions;
          }),
          catchError((err) =>
            of(basketActions.loadBasketAccountsFailure({ error: err.message })),
          ),
        ),
      ),
    ),
  );

  public resetBasketAccountsOnClearActive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clearActiveAccount),
      map(() =>
        basketActions.setActiveBasketAccounts({
          basketAccounts: [],
        }),
      ),
    ),
  );
}
